import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';
import '../styles.css';
import { AUTH_API_URL as BACKEND_URL } from '../config';

const CHECKOUT_PATH = '/create-checkout-session';
const PORTAL_PATH = '/create-portal-session';
const PROFILE_PATH = '/profile';

const STRIPE_PRICE_ID_STANDARD_MONTHLY = 'price_1Rac8cKOKEpIFeC6won6jf0F';
const STRIPE_PRICE_ID_STANDARD_YEARLY = 'price_1Rd6F3KOKEpIFeC6K3UAx9EY';

const STRIPE_PRICE_ID_FINANCE_MONTHLY = 'price_1TcUP5KOKEpIFeC6ZexzbJB3';
const STRIPE_PRICE_ID_FINANCE_YEARLY = 'price_1TcUP5KOKEpIFeC6eOn1GMbo';

const STRIPE_PRICE_ID_VISION_MONTHLY = 'price_1SDQBhKOKEpIFeC6IN4J0h10';
const STRIPE_PRICE_ID_VISION_YEARLY = 'price_1SDQDlKOKEpIFeC6T4kJFB01';

const STRIPE_PUBLIC_KEY = 'pk_live_51RZXn1KOKEpIFeC65vegKH2Mknd803VrDTXzIiEYNlRSeQ5NJvUFSB1fi4PASo5ZnNslcRitgLpB9t0fDNQJlxJU00qWtRnZVM';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [billingCycle, setBillingCycle] = useState('monthly');
  const [busy, setBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setPlan(null);
          return;
        }

        const res = await fetch(`${BACKEND_URL}${PROFILE_PATH}`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });

        if (!res.ok) {
          setPlan(null);
          return;
        }

        const data = await res.json();
        setPlan(data?.plan || null);
      } catch (error) {
        console.error('Failed to fetch user plan:', error);
        setPlan(null);
      }
    };

    fetchPlan();
  }, []);

  const safeJson = async (res) => {
    try {
      return await res.json();
    } catch {
      return null;
    }
  };

  const handleCheckout = async (priceId) => {
    try {
      if (!priceId) {
        throw new Error('ID du prix manquant');
      }

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      setBusy(true);

      const res = await fetch(`${BACKEND_URL}${CHECKOUT_PATH}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ priceId }),
        credentials: 'include',
      });

      if (!res.ok) {
        const errBody = (await safeJson(res)) || { error: await res.text() };
        throw new Error(errBody.details || errBody.error || 'Erreur lors de la création de la session Stripe');
      }

      const data = await res.json();
      const sessionId = data.sessionId || data.id;
      if (!sessionId) {
        throw new Error('Réponse inattendue du serveur (pas de sessionId)');
      }

      const stripe = await loadStripe(STRIPE_PUBLIC_KEY);
      if (!stripe) {
        throw new Error('Impossible de charger Stripe.js');
      }

      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      alert(error?.message || 'Impossible de lancer le paiement.');
    } finally {
      setBusy(false);
    }
  };

  const handleOpenPortal = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      setPortalBusy(true);

      const res = await fetch(`${BACKEND_URL}${PORTAL_PATH}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = (await safeJson(res)) || {};
      if (!res.ok || !data.url) {
        throw new Error(data.details || data.error || 'Impossible d’ouvrir le portail Stripe.');
      }

      window.location.href = data.url;
    } catch (error) {
      console.error('Portal open failed:', error);
      alert(error.message || 'Erreur lors de l’ouverture du portail.');
    } finally {
      setPortalBusy(false);
    }
  };

  const isStandard = plan === 'standard';
  const isPremium = plan === 'premium';
  const isVision = plan === 'vision' || isPremium;
  const isFinance = plan === 'finance';

  const subscribeDisabledStandard =
    busy || portalBusy || isStandard || isVision || isFinance ||
    (!STRIPE_PRICE_ID_STANDARD_MONTHLY && !STRIPE_PRICE_ID_STANDARD_YEARLY);

  const subscribeDisabledFinance =
    busy || portalBusy || isFinance ||
    (!STRIPE_PRICE_ID_FINANCE_MONTHLY && !STRIPE_PRICE_ID_FINANCE_YEARLY);

  const subscribeDisabledVision =
    busy || portalBusy || isVision || isFinance ||
    (!STRIPE_PRICE_ID_VISION_MONTHLY && !STRIPE_PRICE_ID_VISION_YEARLY);

  return (
    <div className="subscription-page">
      <h1 className="subscription-title">{t('subscription.title')}</h1>

      <div className="billing-toggle">
        <div className="toggle-buttons">
          <button
            className={`toggle-button ${billingCycle === 'monthly' ? 'active' : ''}`}
            onClick={() => setBillingCycle('monthly')}
            disabled={busy || portalBusy}
          >
            {t('subscription.monthly')}
          </button>
          <button
            className={`toggle-button ${billingCycle === 'yearly' ? 'active' : ''}`}
            onClick={() => setBillingCycle('yearly')}
            disabled={busy || portalBusy}
          >
            {t('subscription.yearly')}
          </button>
        </div>
      </div>

      <div className="subscription-plans">
        <div className="plan-card plan-card-freemium">
          <h2 className="plan-title">{t('subscription.freemium_title')}</h2>
          <p className="plan-description">{t('subscription.freemium_desc')}</p>
          <p className="plan-price">{t('subscription.freemium_price')}</p>
          <button
            className="subscribe-button standard"
            type="button"
            disabled
            aria-disabled="true"
          >
            {t('subscription.current_plan')}
          </button>
        </div>

        <div className="plan-card">
          <h2 className="plan-title">{t('subscription.standard_title')}</h2>
          <p className="plan-description">{t('subscription.standard_desc')}</p>
          <p className="plan-price">
            {billingCycle === 'monthly' ? (
              <>9.90 € / {t('subscription.month')}</>
            ) : (
              <>
                99.00 € / {t('subscription.year')}
                <span className="save-text">{t('subscription.save_yearly')}</span>
              </>
            )}
          </p>

          <button
            className="subscribe-button standard"
            onClick={() =>
              handleCheckout(
                billingCycle === 'monthly'
                  ? STRIPE_PRICE_ID_STANDARD_MONTHLY
                  : STRIPE_PRICE_ID_STANDARD_YEARLY
              )
            }
            disabled={subscribeDisabledStandard}
            style={(isStandard || isVision || isFinance) ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
            title={
              isStandard
                ? (t('subscription.already_subscribed') || 'Déjà abonné')
                : (
                  isVision
                    ? (t('subscription.already_on_vision') || 'Abonnement Upya Vision actif')
                    : (isFinance ? (t('subscription.already_on_finance') || 'Abonnement Upya Finance actif') : undefined)
                )
            }
            aria-disabled={subscribeDisabledStandard}
          >
            {busy ? t('subscription.loading') || 'Chargement…' : t('subscription.subscribe_standard')}
          </button>
        </div>

        <div className="plan-card">
          <h2 className="plan-title">{t('subscription.finance_title', 'Upya Finance')}</h2>
          <p className="plan-description">{t('subscription.finance_desc', 'Accédez à UpyaChat Finance pour évaluer les risques et structurer les échéanciers des prêts agricoles.')}</p>
          <p className="plan-price">
            {billingCycle === 'monthly' ? (
              <>11.90 € / {t('subscription.month')}</>
            ) : (
              <>
                120.00 € / {t('subscription.year')}
                <span className="save-text">{t('subscription.save_yearly_finance')}</span>
              </>
            )}
          </p>

          <button
            className="subscribe-button standard"
            onClick={() =>
              handleCheckout(
                billingCycle === 'monthly'
                  ? STRIPE_PRICE_ID_FINANCE_MONTHLY
                  : STRIPE_PRICE_ID_FINANCE_YEARLY
              )
            }
            disabled={subscribeDisabledFinance}
            style={isFinance ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
            title={
              isFinance
                ? (t('subscription.already_on_finance') || 'Abonnement Upya Finance actif')
                : undefined
            }
            aria-disabled={subscribeDisabledFinance}
          >
            {busy ? t('subscription.loading') || 'Chargement…' : t('subscription.subscribe_finance', 'Souscrire à Upya Finance')}
          </button>
        </div>

        <div className="plan-card">
          <h2 className="plan-title">{t('subscription.premium_title', 'Upya Vision')}</h2>
          <p className="plan-description">{t('subscription.premium_desc', 'Analyses avancées et visionnage étendu.')}</p>
          <p className="plan-price">
            {billingCycle === 'monthly' ? (
              <>19.90 € / {t('subscription.month')}</>
            ) : (
              <>
                199.00 € / {t('subscription.year')}
                <span className="save-text">{t('subscription.save_yearly2') || ''}</span>
              </>
            )}
          </p>

          <button
            className="subscribe-button standard"
            onClick={() =>
              handleCheckout(
                billingCycle === 'monthly'
                  ? STRIPE_PRICE_ID_VISION_MONTHLY
                  : STRIPE_PRICE_ID_VISION_YEARLY
              )
            }
            disabled={subscribeDisabledVision}
            style={isVision ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
            title={
              isVision
                ? (t('subscription.already_subscribed') || 'Déjà abonné')
                : undefined
            }
            aria-disabled={subscribeDisabledVision}
          >
            {busy ? t('subscription.loading') || 'Chargement…' : t('subscription.subscribe_premium', 'Souscrire à Upya Vision')}
          </button>
        </div>
      </div>

      {(!STRIPE_PRICE_ID_STANDARD_MONTHLY && !STRIPE_PRICE_ID_STANDARD_YEARLY) && (
        <p style={{ color: 'red' }}>
          {t('subscription.error_price_id')}
        </p>
      )}
      {(!STRIPE_PRICE_ID_VISION_MONTHLY && !STRIPE_PRICE_ID_VISION_YEARLY) && (
        <p style={{ color: 'red' }}>
          {t('subscription.error_price_id', 'IDs de prix Stripe manquants pour Upya Vision')}
        </p>
      )}

      <div className="subscription-button-container">
        <button
          className="subscribe-button standard"
          onClick={handleOpenPortal}
          disabled={portalBusy || busy}
          aria-label="Gérer mon abonnement / Se désabonner"
          title="Gérer mon abonnement / Se désabonner"
        >
          {t('subscription.unsubscribe')}
        </button>
      </div>
    </div>
  );
};

export default SubscriptionPage;
